// src/components/PaymentDispatchIndicator.jsx
import React from 'react';
import {
  isSellerPaymentSettled,
  isSellerPaymentInFlight,
} from '../pages/services/paymentService';

const PaymentDispatchIndicator = ({ orders, stage }) => {
  // Orders whose seller payment is fully settled (new track or legacy markers)
  const paidOrders = orders.filter((order) => isSellerPaymentSettled(order));

  // Orders with the payment in flight (assigned / handedOver / legacy dispatched)
  const dispatchedOrders = orders.filter((order) => isSellerPaymentInFlight(order));

  // Split the in-flight bucket so both order pages show the exact step:
  //   assigned   → payment assigned to a JRE, handover not verified yet
  //   on the way → money handed over; the JRE is carrying it to the seller
  const assignedOrders = dispatchedOrders.filter(
    (order) => order.sellerPaymentStatus === 'assigned',
  );
  const onWayOrders = dispatchedOrders.filter(
    (order) =>
      order.sellerPaymentStatus === 'handedOver' ||
      order.sellerPaymentStatus === 'dispatched',
  );

  const pendingOrders = orders.filter(
    (order) => !isSellerPaymentSettled(order) && !isSellerPaymentInFlight(order),
  );

  if (paidOrders.length === 0 && dispatchedOrders.length === 0) {
    return null; // No payments to display
  }

  const containerStyle = {
    backgroundColor: '#fdf6e3',
    border: '1px solid #f0c36d',
    borderRadius: '8px',
    padding: '6px 10px',
    marginTop: 0,
    marginBottom: 0
  };

  const titleStyle = {
    fontSize: '11px',
    fontWeight: '700',
    color: '#92400e',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  };

  const detailsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))',
    gap: '4px 10px'
  };

  const detailItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0
  };

  const labelStyle = {
    fontSize: '9px',
    color: '#92400e',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap'
  };

  const valueStyle = {
    fontSize: '13px',
    fontWeight: '700',
    color: '#78350f',
    whiteSpace: 'nowrap'
  };

  const statusBadgeStyle = (status) => {
    if (status === 'verified') {
      return {
        backgroundColor: '#d1fae5',
        color: '#065f46',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
      };
    } else if (status === 'dispatched') {
      return {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
      };
    }
    return {};
  };

  // Calculate totals from paid/dispatched orders
  // Prefer the operator's settled snapshot. The old keys (`settlementData.amount`
  // / `.fine`) do not exist — the engine writes `totalAmountSeller` / `fineWeight`
  // — so this card always rendered ₹0.00 and 0.000gm.
  const readNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const relevantOrders = [...paidOrders, ...dispatchedOrders];
  const totalAmount = relevantOrders.reduce((sum, order) => {
    return sum + (readNum(order.settlementData?.totalAmountSeller) ||
                  readNum(order.settlementData?.totalAmount) ||
                  readNum(order.settlementData?.amount) ||
                  readNum(order.totalPaymentAmount));
  }, 0);

  const totalFine = relevantOrders.reduce((sum, order) => {
    return sum + (readNum(order.settlementData?.fineWeight) ||
                  readNum(order.settlementData?.fine) ||
                  readNum(order.fineWeight));
  }, 0);

  // Get JRE and dispatch time from first paid/dispatched order
  const firstPaidOrder = paidOrders[0] || dispatchedOrders[0];
  const jreId = firstPaidOrder?.sellerPaymentJreId || firstPaidOrder?.jreId;
  const dispatchedAt = firstPaidOrder?.paymentDispatchedToSellerAt ||
                       firstPaidOrder?.sellerPaymentHandedOverAt ||
                       firstPaidOrder?.sellerPaymentAssignedAt ||
                       firstPaidOrder?.dispatchedToSellerAt;
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stageEmoji = {
    'PRE_PICKUP': '📦',
    'WAREHOUSE': '🏭',
    'BUYER_PAYMENT': '💰'
  };

  return (
    <div style={containerStyle}>
      <div style={titleStyle}>
        <span>{stageEmoji[stage] || '💸'}</span>
        <span>Seller Payment Dispatched - {stage === 'PRE_PICKUP' ? 'Pre-Pickup' : 'Warehouse'} Stage</span>
      </div>

      <div style={detailsGridStyle}>
        <div style={detailItemStyle}>
          <span style={labelStyle}>Orders Paid</span>
          <span style={valueStyle}>
            {paidOrders.length + dispatchedOrders.length} / {orders.length}
          </span>
        </div>

        <div style={detailItemStyle}>
          <span style={labelStyle}>Total Amount</span>
          <span style={valueStyle}>₹{totalAmount.toFixed(2)}</span>
        </div>

        <div style={detailItemStyle}>
          <span style={labelStyle}>Total Fine Weight</span>
          <span style={valueStyle}>{totalFine.toFixed(3)}gm</span>
        </div>

        {jreId && (
          <div style={detailItemStyle}>
            <span style={labelStyle}>JRE Assigned</span>
            <span style={{ ...valueStyle, fontSize: '14px' }}>{jreId}</span>
          </div>
        )}

        {dispatchedAt && (
          <div style={detailItemStyle}>
            <span style={labelStyle}>Dispatched At</span>
            <span style={{ ...valueStyle, fontSize: '13px' }}>{formatDate(dispatchedAt)}</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {paidOrders.length > 0 && (
          <span style={statusBadgeStyle('verified')}>
            ✅ {paidOrders.length} Verified
          </span>
        )}
        {assignedOrders.length > 0 && (
          <span style={statusBadgeStyle('dispatched')}>
            🕓 {assignedOrders.length} Assigned
          </span>
        )}
        {onWayOrders.length > 0 && (
          <span style={statusBadgeStyle('dispatched')}>
            🚚 {onWayOrders.length} On the Way
          </span>
        )}
        {pendingOrders.length > 0 && (
          <span style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            ⏳ {pendingOrders.length} Pending
          </span>
        )}
      </div>

      {firstPaidOrder?.settlementId && (
        <div style={{ fontSize: '10px', color: '#92400e', fontFamily: 'monospace' }}>
          Settlement: {firstPaidOrder.settlementId}
        </div>
      )}
    </div>
  );
};

export default PaymentDispatchIndicator;
